import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useAuthStore } from '../../store/authStore';
import { FiSearch, FiToggleLeft, FiToggleRight, FiTrash2, FiUser, FiLogIn, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';

export default function AdminStores() {
  const [data, setData] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [userDetail, setUserDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const navigate = useNavigate();
  const authStore = useAuthStore();

  const load = (p = 1, q = '') => {
    api.get(`/admin/stores?page=${p}&search=${q}`).then((res) => setData(res.data));
  };

  useEffect(() => { load(); }, []);

  const handleSearch = () => { setPage(1); load(1, search); };

  const toggleActive = async (store: any) => {
    try {
      const res = await api.post(`/admin/stores/${store.id}/toggle`);
      setData((prev: any) => ({
        ...prev,
        data: prev.data.map((s: any) => s.id === store.id ? { ...s, is_active: res.data.is_active } : s),
      }));
      toast.success(`Store ${res.data.is_active ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to toggle store');
    }
  };

  const deleteStore = async (store: any) => {
    if (!window.confirm(`Delete store "${store.name}" and ALL its data? This cannot be undone.`)) return;
    if (!window.confirm(`Are you SURE? This will delete all products, orders, customers, and settings for "${store.name}".`)) return;
    try {
      await api.delete(`/admin/stores/${store.id}`);
      setData((prev: any) => ({ ...prev, data: prev.data.filter((s: any) => s.id !== store.id) }));
      toast.success('Store deleted');
    } catch {
      toast.error('Failed to delete store');
    }
  };

  const viewUser = async (userId: number) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setUserDetail(res.data);
    } catch {
      toast.error('Failed to load user info');
    } finally { setLoadingDetail(false); }
  };

  const impersonateUser = async (userId: number) => {
    try {
      const res = await api.post(`/admin/users/${userId}/impersonate`);
      // Save current admin session
      const adminToken = localStorage.getItem('token');
      const adminUser = JSON.stringify(authStore.user);
      localStorage.setItem('admin_return_token', adminToken || '');
      localStorage.setItem('admin_return_user', adminUser);
      // Switch to impersonated user
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('impersonating', 'true');
      window.location.href = '/dashboard';
    } catch {
      toast.error('Failed to impersonate user');
    }
  };

  if (!data) return <div className="empty-state"><p>Loading...</p></div>;

  return (
    <div>
      <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700 }}>Stores</h1>

      <div className="admin-search">
        <input
          placeholder="Search by name, slug, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button className="btn btn-primary" onClick={handleSearch}><FiSearch /></button>
      </div>

      <div className="card">
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Store</th>
                <th>Owner</th>
                <th>Plan</th>
                <th>Products</th>
                <th>Orders</th>
                <th>Active</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((store: any) => (
                <tr key={store.id}>
                  <td>{store.id}</td>
                  <td>
                    <div><strong>{store.name}</strong></div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{store.slug}.cartz.cc</div>
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {store.user?.first_name} {store.user?.last_name}
                    <br/><span style={{ color: 'var(--gray-400)', fontSize: 11 }}>{store.user?.email}</span>
                  </td>
                  <td>
                    <span style={{
                      padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: store.plan === 'enterprise' ? '#8b5cf618' : store.plan === 'professional' ? '#3b82f618' : store.plan === 'basic' ? '#10b98118' : '#94a3b818',
                      color: store.plan === 'enterprise' ? '#8b5cf6' : store.plan === 'professional' ? '#3b82f6' : store.plan === 'basic' ? '#10b981' : '#94a3b8',
                      textTransform: 'capitalize',
                    }}>
                      {store.plan || 'none'}
                    </span>
                  </td>
                  <td>{store.products_count}</td>
                  <td>{store.orders_count}</td>
                  <td>
                    <button
                      onClick={() => toggleActive(store)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: store.is_active ? '#10b981' : '#ef4444' }}
                      title={store.is_active ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                    >
                      {store.is_active ? <FiToggleRight /> : <FiToggleLeft />}
                    </button>
                  </td>
                  <td style={{ fontSize: 13 }}>{new Date(store.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-outline" title="View user info" onClick={() => store.user?.id && viewUser(store.user.id)}>
                        <FiUser />
                      </button>
                      <button className="btn btn-sm btn-outline" title="Login as user" onClick={() => store.user?.id && impersonateUser(store.user.id)}
                        style={{ color: '#3b82f6', borderColor: '#3b82f6' }}>
                        <FiLogIn />
                      </button>
                      <button className="btn btn-sm btn-outline" title="Delete store" onClick={() => deleteStore(store)}
                        style={{ color: '#ef4444', borderColor: '#ef4444' }}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.last_page > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          {Array.from({ length: data.last_page }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`btn ${p === data.current_page ? 'btn-primary' : 'btn-outline'}`}
              style={{ minWidth: 36, padding: '6px 10px', fontSize: 13 }}
              onClick={() => { setPage(p); load(p, search); }}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* User Detail Modal */}
      {userDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setUserDetail(null)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 560, width: '95%', maxHeight: '85vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>User Info</h2>
              <button onClick={() => setUserDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}><FiX /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: 20 }}>
              <div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Name</div><strong>{userDetail.user.first_name} {userDetail.user.last_name}</strong></div>
              <div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Email</div><strong>{userDetail.user.email}</strong></div>
              <div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Role</div><strong style={{ textTransform: 'capitalize' }}>{userDetail.user.role}</strong></div>
              <div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Company</div><strong>{userDetail.user.company_name || '-'}</strong></div>
              <div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Registered</div><strong>{new Date(userDetail.user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong></div>
            </div>

            {/* Stores */}
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px', borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>Stores</h3>
            {userDetail.stores.map((s: any) => (
              <div key={s.id} style={{ padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8, marginBottom: 8, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{s.name}</strong>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: s.is_active ? '#10b98118' : '#ef444418',
                    color: s.is_active ? '#10b981' : '#ef4444',
                  }}>{s.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <div style={{ color: 'var(--gray-400)', fontSize: 12, marginTop: 4 }}>
                  {s.slug}.cartz.cc · Plan: {s.plan || 'none'} · {s.products_count} products · {s.orders_count} orders
                  {s.custom_domain && <> · Domain: {s.custom_domain}</>}
                  {s.trial_ends_at && <> · Trial ends: {new Date(s.trial_ends_at).toLocaleDateString()}</>}
                </div>
              </div>
            ))}

            {/* Subscriptions */}
            {userDetail.subscriptions.length > 0 && (
              <>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: '16px 0 8px', borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>Payment History</h3>
                {userDetail.subscriptions.map((sub: any) => (
                  <div key={sub.id} style={{ padding: '8px 14px', background: 'var(--gray-50)', borderRadius: 8, marginBottom: 6, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ textTransform: 'capitalize' }}>{sub.plan}</strong> · {Number(sub.amount).toFixed(3)} {sub.currency} · {sub.payment_method}
                      {sub.promo_code && <span style={{ color: '#16a34a' }}> · Promo: {sub.promo_code}</span>}
                    </div>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: sub.payment_status === 'paid' ? '#10b98118' : sub.payment_status === 'pending' ? '#f59e0b18' : '#ef444418',
                      color: sub.payment_status === 'paid' ? '#10b981' : sub.payment_status === 'pending' ? '#f59e0b' : '#ef4444',
                    }}>{sub.payment_status}</span>
                  </div>
                ))}
              </>
            )}

            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { setUserDetail(null); impersonateUser(userDetail.user.id); }}>
                <FiLogIn style={{ marginRight: 6 }} /> Login as User
              </button>
              <button className="btn btn-outline" onClick={() => setUserDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
